import type { BuyerEngagementSummary, DocumentPageStats } from '@dataroom/shared';

/** ADR-0038 buyer engagement — the few words the admin's Activity views use. */

/** "3 of 4 (75%)" — opened out of what the buyer can open today; "None visible"
 * when their access shows them nothing. */
export function openedOfVisibleLabel(b: BuyerEngagementSummary): string {
  if (b.openedPercent === null) return 'None visible';
  return `${b.openedOfVisible} of ${b.visibleDocuments} (${b.openedPercent}%)`;
}

/** "Access revoked" / "Access expired" for a buyer whose access has ended
 * (their history stays listed); null while it is live. */
export function accessEndedLabel(b: Pick<BuyerEngagementSummary, 'accessEnded'>): string | null {
  if (b.accessEnded === 'revoked') return 'Access revoked';
  if (b.accessEnded === 'expired') return 'Access expired';
  return null;
}

/** Plain plural: "1 view", "3 downloads". */
export function countLabel(n: number, singular: string): string {
  return `${n} ${singular}${n === 1 ? '' : 's'}`;
}

/** "<1 min", "4 min", "1 h 5 min" — whole minutes (the server's wording, ADR-0056). */
export function readingTimeLabel(seconds: number): string {
  if (seconds < 60) return '<1 min';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

/** "Pages viewed (on screen): 12 of 40 · total time 6 min" (ADR-0056). */
export function pagesViewedLabel(
  stats: Pick<DocumentPageStats, 'pagesViewed' | 'pageCount' | 'seconds'>,
): string {
  return `Pages viewed (on screen): ${stats.pagesViewed} of ${stats.pageCount} · total time ${readingTimeLabel(stats.seconds)}`;
}

/** Most recent activity first; buyers with none last; then by name (ADR-0056). */
export function byMostRecentActivity(
  a: BuyerEngagementSummary,
  b: BuyerEngagementSummary,
  label: (x: BuyerEngagementSummary) => string,
): number {
  const at = a.lastActiveAt ? Date.parse(a.lastActiveAt) : -Infinity;
  const bt = b.lastActiveAt ? Date.parse(b.lastActiveAt) : -Infinity;
  if (at !== bt) return bt > at ? 1 : -1;
  return label(a).localeCompare(label(b));
}

/** An alert gap in words: "15 minutes", "1 hour", "24 hours" (ADR-0056). */
export function intervalLabel(minutes: number): string {
  if (minutes < 60) return `${minutes} minutes`;
  const hours = minutes / 60;
  return hours === 1 ? '1 hour' : `${hours} hours`;
}
